/* eslint-disable react/prop-types */
import { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { ArrowLeft, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import { Checkbox } from "src/components/ui/checkbox";
import isEmpty from "lodash.isempty";
import ColourPickerStep from "src/features/vendorSponsorshipAdmin/components/ColorPickerStep/ColorPickerStep";
import { HashtagFormValues } from "../../adapters/formValuesToHashtag";
import { safeImageUrl } from "src/utils/safeImageUrl";

export type { HashtagFormValues };

interface Props {
	defaultValues: HashtagFormValues;
	onSubmit: (data: HashtagFormValues, imageRemoved: boolean) => void;
	isSubmitting?: boolean;
	isEdit?: boolean;
	onDelete?: () => void;
	existingImageUrl?: string | null;
	existingNames?: string[];
	currentName?: string;
}

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_IMAGE_SIZE = 1_048_576; // 1 MB

const HashtagForm: FC<Props> = ({
	defaultValues,
	onSubmit,
	isSubmitting = false,
	isEdit = false,
	onDelete,
	existingImageUrl,
	existingNames = [],
	currentName
}) => {
	const methods = useForm<HashtagFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit, watch, setValue } = methods;
	const { isDirty, errors } = useFormState({ control });

	const fileInputRef = useRef<HTMLInputElement>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [imageRemoved, setImageRemoved] = useState(false);
	const [imageError, setImageError] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);

	const imageFile = watch("image");

	const safeExistingUrl = safeImageUrl(existingImageUrl);
	const displayUrl = imageRemoved ? null : previewUrl || safeExistingUrl;

	const isSubmitBtnDisabled =
		(!isDirty && !imageRemoved) ||
		!isEmpty(errors) ||
		isSubmitting ||
		!!imageError;

	useEffect(() => {
		reset(defaultValues);
		setImageRemoved(false);
		setImageError(null);
	}, [defaultValues, reset]);

	useEffect(() => {
		if (imageFile) {
			const url = URL.createObjectURL(imageFile);
			setPreviewUrl(url);
			return () => URL.revokeObjectURL(url);
		} else {
			setPreviewUrl(null);
		}
	}, [imageFile]);

	const validateAndSetFile = (file: File) => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			setImageError("Only JPG and PNG files are allowed.");
			setValue("image", null);
			setPreviewUrl(null);
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			setImageError("Image must be under 1 MB.");
			setValue("image", null);
			setPreviewUrl(null);
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		setImageError(null);
		setValue("image", file, { shouldDirty: true });
		setImageRemoved(false);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) validateAndSetFile(file);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files[0];
		if (file) validateAndSetFile(file);
	};

	const handleRemoveImage = () => {
		setValue("image", null, { shouldDirty: true });
		setPreviewUrl(null);
		setImageRemoved(true);
		setImageError(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	return (
		<form
			onSubmit={handleSubmit((data) => onSubmit(data, imageRemoved))}
			onKeyDown={(e) => {
				if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
					e.preventDefault();
				}
			}}
		>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-4">
					{/* Details */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.name || errors.color
								? "border-red-400/60"
								: "border-border"
						].join(" ")}
					>
						{(errors.name || errors.color) && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.name?.message ||
										"Please fill out the required fields"}
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Details
							</label>

							<div className="flex flex-col gap-5">
								<FormField
									control={control}
									name="name"
									rules={{
										required: "Name is required",
										maxLength: {
											value: 100,
											message: "Maximum 100 characters"
										},
										validate: (value: string) => {
											const isDuplicate = existingNames.some(
												(n) =>
													n.toLowerCase() === value.toLowerCase() &&
													n.toLowerCase() !== (currentName ?? "").toLowerCase()
											);
											if (isDuplicate) {
												return "A hashtag with this name already exists";
											}
											return true;
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Name
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Hashtag name (without #)"
													maxLength={100}
													className="w-full bg-white"
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-500 text-right mt-1">
												{field.value?.length || 0} / 100
											</p>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="description"
									rules={{
										maxLength: {
											value: 2000,
											message: "Maximum 2000 characters"
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Description
											</FormLabel>
											<FormControl>
												<textarea
													placeholder="Feed description text"
													maxLength={2000}
													rows={4}
													className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 overflow-hidden resize-none"
													onInput={(e) => {
														const target = e.target as HTMLTextAreaElement;
														target.style.height = "auto";
														target.style.height = `${target.scrollHeight}px`;
													}}
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-500 text-right mt-1">
												{field.value?.length || 0} / 2000
											</p>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="color"
									rules={{
										required: "Colour is required"
									}}
									render={() => (
										<FormItem>
											<ColourPickerStep name="color" label="Colour" />
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>

					{/* Settings */}
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Settings
							</label>

							<div className="flex flex-col gap-4">
								<FormField
									control={control}
									name="isFavourite"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<FormLabel className="!mt-0 text-sm text-zinc-700 cursor-pointer">
												Show this hashtag in the favourites feed bar
											</FormLabel>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="isPrivate"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<FormLabel className="!mt-0 text-sm text-zinc-700 cursor-pointer">
												Private hashtags are not shown to all users
											</FormLabel>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="excludeFromMainFeed"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<FormLabel className="!mt-0 text-sm text-zinc-700 cursor-pointer">
												Posts with this hashtag will not appear in the main feed
											</FormLabel>
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>

					{/* Image */}
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Image (optional)
							</label>
							<p className="text-[13px] text-muted-foreground mb-4">
								JPG or PNG, 1000 x 566px recommended, under 1 MB
							</p>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/jpeg,image/jpg,image/png"
								className="hidden"
								data-testid="hashtag-file-input"
								onChange={handleFileChange}
							/>
							<div className="max-w-[480px]">
								{displayUrl ? (
									<>
										<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
											<img
												src={displayUrl}
												alt="Uploaded image preview"
												className="object-contain w-full h-auto max-h-[400px]"
											/>
										</div>
										<div className="flex items-center gap-2">
											<button
												type="button"
												className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
												onClick={() => fileInputRef.current?.click()}
											>
												Change image
											</button>
											<button
												type="button"
												className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
												onClick={handleRemoveImage}
											>
												Remove
											</button>
										</div>
									</>
								) : (
									<div
										role="button"
										tabIndex={0}
										onClick={() => fileInputRef.current?.click()}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												fileInputRef.current?.click();
											}
										}}
										onDragOver={handleDragOver}
										onDragLeave={handleDragLeave}
										onDrop={handleDrop}
										className={[
											"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
											isDragging
												? "border-primary bg-primary-lighter"
												: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50"
										].join(" ")}
									>
										<Upload className="h-6 w-6 text-muted-foreground" />
										<div>
											<p className="text-sm font-medium text-foreground">
												Drag image here or click to browse
											</p>
											<p className="text-xs text-muted-foreground mt-1">
												JPG or PNG, under 1 MB
											</p>
										</div>
									</div>
								)}
							</div>
							{imageError && (
								<p className="text-sm text-red-500 mt-2">{imageError}</p>
							)}
						</div>
					</div>
				</div>

				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<div className="flex items-center gap-4">
						<Link
							to="/social-feed/hashtags"
							className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back to hashtags
						</Link>

						{isEdit && onDelete && (
							<Button type="button" variant="destructive" onClick={onDelete}>
								Delete
							</Button>
						)}
					</div>

					<Button
						type="submit"
						data-testid="save-button"
						disabled={isSubmitBtnDisabled}
					>
						{isSubmitting ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						) : (
							"Save"
						)}
					</Button>
				</div>
			</FormProvider>
		</form>
	);
};

export default HashtagForm;
